import type { AiConfigPhaseKey } from '@/components/station/schema';
import { type PromptFieldsFragment, PromptFormat } from '@/graphql/gql/graphql';
import type { PromptProductFilter } from '@/store/prompt';

export const AI_CONFIG_PHASE_TYPE_ID = {
  patient: 1,
  examiner: 2,
  markscheme: 3,
  result: 4,
  examination: 5,
  // turn_router: 6,
} as const satisfies Record<AiConfigPhaseKey, number>;

export const PROMPT_TYPE_OPTIONS = [
  { key: '1', label: 'Patient' },
  { key: '2', label: 'Examiner' },
  { key: '3', label: 'Markscheme' },
  { key: '4', label: 'Result' },
  { key: '5', label: 'Examination' },
  { key: '6', label: 'Quiz summary' },
  { key: '7', label: 'Content' },
  { key: '8', label: 'Tutor' },
] as const;

export const PROMPT_TYPE_FILTER_OPTIONS = [{ key: '', label: 'All types' }, ...PROMPT_TYPE_OPTIONS] as const;

type PromptForProductMatch = Pick<PromptFieldsFragment, 'name' | 'code'>;

interface PromptProductMatcher {
  key: Exclude<PromptProductFilter, 'all'>;
  label: string;
  match: (prompt: PromptForProductMatch) => boolean;
}

const hasBracketTag = (tag: string) => (prompt: PromptForProductMatch) =>
  prompt.name.toUpperCase().includes(`[${tag}`);

const _hasCodePrefix = (prefix: string) => (prompt: PromptForProductMatch) =>
  (prompt.code ?? '').startsWith(prefix);

// Single source of truth for "which product does this prompt belong to".
// Add a new product line by adding one entry here — nothing else needs to change.
export const PROMPT_PRODUCT_MATCHERS: readonly PromptProductMatcher[] = [
  { key: 'osce', label: 'OSCE', match: hasBracketTag('OSCE') },
  { key: 'pace', label: 'PACE', match: hasBracketTag('PACE') },
  { key: 'quiz', label: 'Quiz', match: hasBracketTag('QUIZ') },
  { key: 'content', label: 'Content', match: hasBracketTag('CONTENT') },
  { key: 'tutor', label: 'Tutor', match: hasBracketTag('TUTOR') },
];

export const PROMPT_PRODUCT_FILTER_OPTIONS = [
  { key: 'all', label: 'All products' },
  ...PROMPT_PRODUCT_MATCHERS.map(({ key, label }) => ({ key, label })),
] as const satisfies ReadonlyArray<{ key: PromptProductFilter; label: string }>;

export function matchesPromptProductFilter(
  prompt: PromptForProductMatch,
  productFilter: PromptProductFilter,
): boolean {
  if (productFilter === 'all') return true;
  return PROMPT_PRODUCT_MATCHERS.find((m) => m.key === productFilter)?.match(prompt) ?? false;
}

export function getPromptTypeLabel(typeId: number | null | undefined): string {
  if (typeId == null) return '—';
  const match = PROMPT_TYPE_OPTIONS.find((opt) => Number(opt.key) === typeId);
  return match?.label ?? `Type ${typeId}`;
}

export function getPromptTypeChipColor(
  typeId: number | null | undefined,
): 'default' | 'secondary' | 'success' | 'warning' {
  switch (typeId) {
    case 1:
      return 'secondary';
    case 2:
      return 'success';
    case 3:
      return 'warning';
    default:
      return 'default';
  }
}

export function parsePromptNumericId(id: string | null | undefined): number | null {
  if (id == null || id === '') return null;
  const n = Number.parseInt(id, 10);
  return Number.isFinite(n) ? n : null;
}

export function formatPromptUserName(
  user?: {
    firstName?: string | null;
    lastName?: string | null;
    username?: string | null;
  } | null,
): string {
  if (!user) return '—';
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  if (fullName) return fullName;
  return user.username ?? '—';
}

export function promptTextExcerpt(text: string | null | undefined, maxLength: number): string {
  if (!text) return '';
  const normalized = text.replace(/\s+/g, ' ').trim();
  if (normalized.length <= maxLength) return normalized;
  return `${normalized.slice(0, maxLength)}…`;
}

// Shared surface for the prompt text editors (markdown textarea, markdown preview, CodeMirror JSON).
export const PROMPT_EDITOR_SURFACE = 'bg-content2';

export const PROMPT_FORMAT_OPTIONS = [
  { key: PromptFormat.text, label: 'Text (Markdown)' },
  { key: PromptFormat.json, label: 'JSON' },
];
